import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "../../../utils/hooks/useAuth";
import { UserContext } from "../../../utils/contexts/UserContext";
import LanguageSelector from "../../../components/LanguageSelector/LanguageSelector";
import {
  getFederatedProviders,
  startFederatedLogin,
} from "../../../services/federatedService";
import { PROVIDER_NAME_STORAGE_KEY, STATE_STORAGE_KEY } from "../federatedState";
import { federatedRefusalMessage } from "../../../utils/helpers/federatedErrors";
import { takeReturnTo, takeSessionExpired } from "../../../utils/helpers/sessionHandoff";
import telosWordmark from "../../../assets/telos-wordmark.svg";
import BIFIcon from "../../../components/Icons/BIFIcon";

// Inline SVGs (project convention is no lucide-react).
const svgProps = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  viewBox: "0 0 24 24",
  "aria-hidden": "true",
};
const UserIcon = ({ className }) => (
  <svg className={className} {...svgProps}>
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const LockIcon = ({ className }) => (
  <svg className={className} {...svgProps}>
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const ArrowLeftIcon = ({ className }) => (
  <svg className={className} {...svgProps}>
    <path d="M19 12H5" />
    <path d="m12 19-7-7 7-7" />
  </svg>
);
const EyeIcon = ({ className }) => (
  <svg className={className} {...svgProps}>
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const EyeOffIcon = ({ className }) => (
  <svg className={className} {...svgProps}>
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
    <line x1="2" x2="22" y1="2" y2="22" />
  </svg>
);

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { loginUser, error } = useAuth();

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [providers, setProviders] = useState([]);
  const [redirecting, setRedirecting] = useState(null);
  const [federatedError, setFederatedError] = useState(null);
  // Read once on mount, not on every render: both are one-shot and clearing
  // them here is what stops the banner outliving the sign-in it explains.
  const [expiredSession] = useState(takeSessionExpired);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { username: "", password: "" } });

  useEffect(() => {
    if (user?.uuid) navigate(takeReturnTo() || "/", { replace: true });
  }, [user, navigate]);

  // Failing quietly is deliberate: password sign-in must keep working when the
  // provider list is unreachable, and an error banner about a button nobody
  // asked for would only get in the way.
  useEffect(() => {
    const controller = new AbortController();
    getFederatedProviders({ signal: controller.signal })
      .then((list) => setProviders(Array.isArray(list) ? list : []))
      .catch(() => {});
    return () => controller.abort();
  }, []);

  const onFederated = async (slug, name) => {
    setRedirecting(slug);
    setFederatedError(null);
    try {
      const { authorization_url: url, state } = await startFederatedLogin({ slug });
      sessionStorage.setItem(STATE_STORAGE_KEY, state);
      sessionStorage.setItem(PROVIDER_NAME_STORAGE_KEY, name || slug);
      window.location.assign(url);
    } catch (err) {
      setFederatedError(
        federatedRefusalMessage(err, t, t("auth.federated.startFailed"), {
          provider: name || slug,
        })
      );
      setRedirecting(null);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const { redirectPath } = await loginUser(data);
      navigate(takeReturnTo() || redirectPath, { replace: true });
    } catch {
      // error surfaced via the `error` state from useAuth
    } finally {
      setLoading(false);
    }
  };

  // 429 is checked before `detail`: the throttle answers with DRF's English
  // prose ("Request was throttled. Expected available in N seconds"), and the
  // one thing the person needs to know — that the way back in is a new password
  // link from their administrator — is not in it.
  const serverError =
    error?.status === 429
      ? t("auth.tooManyAttempts")
      : error?.data?.detail ||
        (error?.status === 401 ? t("auth.invalidCredentials") : null);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-sky-50 to-blue-100 p-4">
      <Link
        to="/"
        className="fixed top-5 left-5 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm text-slate-500 hover:text-slate-800 hover:bg-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00adff]/50 transition"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        {t("auth.backToLanding")}
      </Link>
      <div className="w-full max-w-md">
        {/* The switcher also lives in BaseLayout, but that sits behind
            ProtectedRoute — so without this a user whose browser reported the
            wrong language has no way to correct it before signing in. */}
        <div className="flex justify-end mb-3">
          <LanguageSelector />
        </div>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="bg-white rounded-2xl shadow-xl shadow-slate-200/60 p-8 space-y-5 border border-slate-100"
        >
          <div className="text-center pb-2">
            <img
              src={telosWordmark}
              alt="Telos"
              className="h-10 w-auto mx-auto"
            />
            <p className="mt-3 text-sm text-slate-500">{t("auth.subtitle")}</p>
          </div>

          {/* Amber, not rose: the session ending is not the person's error,
              and the form below still works exactly as it always does. */}
          {expiredSession && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
              {t("auth.sessionExpired")}
            </div>
          )}

          <div>
            <label
              htmlFor="username"
              className="block text-xs font-semibold text-slate-700 mb-2"
            >
              {t("auth.username")}
            </label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                id="username"
                type="text"
                autoComplete="username"
                disabled={loading}
                {...register("username", { required: t("auth.usernameRequired") })}
                className="w-full pl-10 pr-3 py-2.5 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#00adff] focus:ring-2 focus:ring-[#00adff]/25 outline-none transition disabled:opacity-60"
                placeholder={t("auth.usernamePlaceholder")}
              />
            </div>
            {errors.username && (
              <p className="mt-1.5 text-xs text-rose-600">
                {errors.username.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-xs font-semibold text-slate-700 mb-2"
            >
              {t("auth.password")}
            </label>
            <div className="relative">
              <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                disabled={loading}
                {...register("password", { required: t("auth.passwordRequired") })}
                className="w-full pl-10 pr-10 py-2.5 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#00adff] focus:ring-2 focus:ring-[#00adff]/25 outline-none transition disabled:opacity-60"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOffIcon className="w-4 h-4" />
                ) : (
                  <EyeIcon className="w-4 h-4" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1.5 text-xs text-rose-600">
                {errors.password.message}
              </p>
            )}
          </div>

          {serverError && (
            <div className="rounded-xl bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
              {serverError}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 rounded-xl bg-[#0f2e54] text-white text-sm font-semibold shadow-lg shadow-[#0f2e54]/30 hover:bg-[#0c2543] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00adff]/50 transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? t("auth.signingIn") : t("auth.signIn")}
          </button>

          {/* Below the password form, not above it: for most people the
              password is the way in, and the divider marks bilorg as the
              alternative rather than the next step. */}
          {providers.length > 0 && (
            <div className="space-y-3 pt-1">
              <div className="flex items-center gap-3">
                <span className="h-px flex-1 bg-slate-200" />
                <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  {t("auth.federated.or")}
                </span>
                <span className="h-px flex-1 bg-slate-200" />
              </div>

              {providers.map((p) => (
                <button
                  key={p.slug}
                  type="button"
                  onClick={() => onFederated(p.slug, p.name)}
                  disabled={Boolean(redirecting) || loading}
                  className="w-full inline-flex items-center justify-center gap-2.5 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 hover:border-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00adff]/50 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <BIFIcon className="h-5 w-5 shrink-0" />
                  {redirecting === p.slug
                    ? t("auth.federated.redirecting")
                    : t("auth.federated.continueWith", { provider: p.name })}
                </button>
              ))}

              {federatedError && (
                <div className="rounded-xl bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
                  {federatedError}
                </div>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
